// runtime-anim.js — particle systems + easing curves for the broadcast overlay
// animations (confetti, fireworks, flying stumps). Pure functions of their
// arguments — no app state, no imports. Split from runtime-core.js.

export function _makeConfetti(count, cx, cy) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 120 + Math.random() * 280;
    const colors = ["#fbbf24","#f59e0b","#10b981","#3b82f6","#ef4444","#ec4899","#8b5cf6","#fff"];
    particles.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 150,
      w: 4 + Math.random() * 6,
      h: 8 + Math.random() * 12,
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 12,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 1
    });
  }
  return particles;
}

export function _makeFirework(count, cx, cy) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 60 + Math.random() * 200;
    const colors = ["#fbbf24","#f97316","#ef4444","#ec4899","#a855f7","#22d3ee","#10b981","#fff"];
    particles.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 80,
      r: 2 + Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 1,
      decay: 0.3 + Math.random() * 0.5
    });
  }
  return particles;
}

export function _makeStumps(cx, cy) {
  const stumps = [];
  for (let i = 0; i < 3; i++) {
    stumps.push({
      x: cx - 20 + i * 20,
      y: cy,
      vx: (Math.random() - 0.5) * 100,
      vy: -200 - Math.random() * 150,
      rot: 0,
      rotV: (Math.random() - 0.5) * 15,
      life: 1
    });
  }
  // Bails
  stumps.push({ x: cx - 10, y: cy - 5, vx: -60 + Math.random()*40, vy: -250 - Math.random()*80, rot: 0, rotV: 18, life: 1, isBail: true });
  stumps.push({ x: cx + 10, y: cy - 5, vx: 60 + Math.random()*40, vy: -250 - Math.random()*80, rot: 0, rotV: -18, life: 1, isBail: true });
  return stumps;
}

export function _drawConfetti(ctx, particles, elapsed, duration) {
  const now = Date.now();
  const dt = (now - (particles._lastFrame || (now - 16))) / 1000;
  particles._lastFrame = now;
  const fadeStart = duration * 0.6;
  particles.forEach(p => {
    p.x += p.vx * dt;
    p.vy += 500 * dt; // gravity
    p.y += p.vy * dt;
    p.rot += p.rotV * dt;
    if (elapsed > fadeStart) p.life = Math.max(0, 1 - (elapsed - fadeStart) / (duration - fadeStart));
    if (p.life <= 0) return;
    ctx.save();
    ctx.globalAlpha = p.life * 0.9;
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h);
    ctx.restore();
  });
}

export function _drawFirework(ctx, particles, elapsed, _duration) {
  const now = Date.now();
  const dt = (now - (particles._lastFrame || (now - 16))) / 1000;
  particles._lastFrame = now;
  particles.forEach(p => {
    p.x += p.vx * dt;
    p.vy += 180 * dt;
    p.y += p.vy * dt;
    p.life = Math.max(0, p.life - p.decay * dt);
    if (p.life <= 0) return;
    ctx.save();
    ctx.globalAlpha = p.life * 0.85;
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 8;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  });
}

export function _drawStumps(ctx, stumps, elapsed) {
  const now = Date.now();
  const dt = (now - (stumps._lastFrame || (now - 16))) / 1000;
  stumps._lastFrame = now;
  stumps.forEach(p => {
    p.x += p.vx * dt;
    p.vy += 600 * dt;
    p.y += p.vy * dt;
    p.rot += p.rotV * dt;
    p.life = Math.max(0, p.life - 0.25 * dt);
    if (p.life <= 0) return;
    ctx.save();
    ctx.globalAlpha = p.life;
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.isBail ? "#b45309" : "#f5f5dc";
    if (p.isBail) {
      ctx.fillRect(-10, -2, 20, 4);
    } else {
      ctx.fillRect(-3, -20, 6, 40);
    }
    ctx.restore();
  });
}

export function easeOutElastic(x) {
  if (x === 0 || x === 1) return x;
  return Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * (2 * Math.PI / 3)) + 1;
}

export function easeOutBounce(x) {
  const n1 = 7.5625, d1 = 2.75;
  if (x < 1/d1) return n1*x*x;
  if (x < 2/d1) return n1*(x-=1.5/d1)*x+0.75;
  if (x < 2.5/d1) return n1*(x-=2.25/d1)*x+0.9375;
  return n1*(x-=2.625/d1)*x+0.984375;
}

export function easeOutBack(x) {
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}
